import React from 'react';
import FullscreenMapView from 'app/components/dashboard/network/FullscreenMapView';
import { SharedMapView } from 'app/components/dashboard/network/SharedMapView';
import {
    CountType,
    FlightDto,
    FlightId,
    FlightLevelBand,
    HotspotDto,
    LoadClassification,
    MapFlightsDataDto,
    MapTrafficVolume,
    MapTrafficVolumeListRequest,
    TimePeriod,
    TrafficType,
} from 'app/model/CORE';
import {
    ConvectionSource,
    ExtendedMonitoredTv,
    MonitoredMeasure,
    NetworkMapConvection,
    MonitoredRegulationOutlook,
    SOL49_MAP_FLIGHTS_DATA_REQUEST_DTO_CLAZZ_VALUE,
    Sol49FlightAirspaceCrossing,
    Sol49MapFlightsDataRequestDto,
} from 'app/model/SOL49';
import { MapSettings } from 'app/components/dashboard/network/map/MapSettings';
import { ProfileSegmentHighlight } from 'app/components/map/model/MapModel';
import Utils from 'app/common/util/Utils';
import TimeUtils from 'app/common/util/TimeUtils';
import { ServiceFactory } from 'app/service/ServiceFactory';
import { notification, Spin } from 'antd';
import MonitoredLoadUtils from 'app/common/util/MonitoredLoadUtils';
import TimePeriodUtils from 'app/common/util/TimePeriodUtils';
import { DEFAULT_FL_FILTER_RANGE } from 'app/components/dashboard/network/AbstractMapView';
import { MonitorableMapTv } from 'app/components/map/util/MapUtils';
import memoizeOne from 'memoize-one';
import AppContext from 'app/common/context/AppContext';
import { UpdateMapCurrentTimeEvent } from 'app/common/eventbus/Events';
import { MapLayerUtils, MeasureMapTrafficVolume } from 'app/components/dashboard/network/map/Map';
import { RootState } from 'app/redux/store';
import { connect, ConnectedProps } from 'react-redux';
import { setLoadingData } from 'app/redux/features/map/fullscreen-slice';
import { NMUI4DContext, NMUI4DContextProps } from 'app/common/context/NMUI4DContext';
import { PathPointLoadClassification } from 'app/unity/ContentViewerTypes/PathPointLoadClassification';

const mapStateToProps = (state: RootState) => {
    return {
        loading: state.mapFullscreenData.loadingData,
    };
};

const mapDispatchToProps = {
    setLoadingData,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface NetworkMonitoringMapProps extends PropsFromRedux {
    fullscreenMap: boolean;
    currentTime: Date;
    selectedTvs: Array<ExtendedMonitoredTv>;
    selectedFlights: Array<FlightDto>;
    monitoredTvs: Array<ExtendedMonitoredTv>;
    tvsOccupancyPeriods?: { [key: string]: Map<FlightId, TimePeriod> };
    visible: boolean;
    convectionData: NetworkMapConvection;
    mapSettings: MapSettings;
    onMapSettingsChanged(mapSettings: MapSettings): void;

    //new
    monitoredPeriod: TimePeriod;
    monitoredMeasures: Array<MonitoredMeasure>;
    selectedMeasures: Array<MonitoredMeasure>;
    hotspots: Array<HotspotDto>;
    selectedHotspots: Array<ExtendedMonitoredTv>;
    regulationOutlook: Array<MonitoredRegulationOutlook>;
    selectedRegulationOutlook: Array<MonitoredRegulationOutlook>;
    textFilter: string;
    tvSetMap: { [key: string]: string };
    dashboardDataVersion: number;
    filteredTvIds: Array<string>;

    onDisplayChanged?(newDisplay: CountType): void;
    onFlightLevelFilterChanged?(flRange: FlightLevelBand): void;
    onMapRefTimePeriodChanged?(newPeriod: TimePeriod): void;
}

//single source of truth for both map display components
interface NetworkMonitoringMapState {
    flightData: MapFlightsDataDto;
    selectedMapTVs: Array<MapTrafficVolume>;
    selectedMeasureMapTVs: Array<MeasureMapTrafficVolume>;
    mapRefTime: Date;
    mapRefTimePeriod: TimePeriod;
    highlightedSegments: Map<FlightId, ProfileSegmentHighlight[]>;
    flightLevelFilter: FlightLevelBand;

    // loading: boolean
    //fullscreen state
    monitorableMapTvs: Array<MonitorableMapTv>;
    timePeriodSelection: boolean;
    display: CountType;
    refreshMapTime: boolean;
    refreshMapTimePeriod: boolean;
    timeAnimation: boolean;
}

export const DEFAULT_INITIAL_STATE = (props: NetworkMonitoringMapProps) => ({
    flightData: undefined,
    selectedMapTVs: [],
    selectedMeasureMapTVs: [],
    mapRefTime: props.currentTime,
    mapRefTimePeriod: { from: props.currentTime, to: props.monitoredPeriod.to },
    highlightedSegments: undefined,
    convectionTime: undefined,
    flightLevelFilter: DEFAULT_FL_FILTER_RANGE,
    monitorableMapTvs: [],
    timePeriodSelection: false,
    display: CountType.ENTRY,
    refreshMapTime: true,
    refreshMapTimePeriod: true,
    timeAnimation: false,
    // loading: false
});

class NetworkMonitoringMap extends React.PureComponent<NetworkMonitoringMapProps, NetworkMonitoringMapState> {
    private interval: NodeJS.Timeout;
    private animationStartTime: Date;
    private ANIMATION_INTERVAL_MS = 800;

    public static contextType = NMUI4DContext;
    private nmui4dContext?: NMUI4DContextProps;

    constructor(props: NetworkMonitoringMapProps) {
        super(props);
        this.state = DEFAULT_INITIAL_STATE(props);
        this.initBus();
    }

    private initBus() {
        AppContext.eventBus.register(UpdateMapCurrentTimeEvent.NAME, (event: UpdateMapCurrentTimeEvent) => {
            //TODO: limit to the boundaries
            if (this.state.timePeriodSelection) {
                //shift time period
                this.setState(prevState => {
                    return {
                        mapRefTimePeriod: TimePeriodUtils.shiftPeriod(prevState.mapRefTimePeriod, event.deltaInMinutes),
                        refreshMapTime: false,
                    };
                });
            } else {
                //shift current time
                this.setState(prevState => {
                    const refTime = TimeUtils.addMinutes(prevState.mapRefTime, event.deltaInMinutes);
                    const newTime = this.forceDateInTimePeriod(refTime, this.props.monitoredPeriod);
                    return {
                        mapRefTime: newTime,
                        refreshMapTimePeriod: false,
                    };
                });
            }
        });
    }

    private forceDateInTimePeriod(refTime: Date, timePeriod: TimePeriod): Date {
        return TimePeriodUtils.contains(timePeriod, refTime)
            ? refTime
            : TimeUtils.compare(refTime, timePeriod.from) < 0
              ? timePeriod.from
              : TimeUtils.min(refTime, timePeriod.to);
    }

    refreshMapFlightData = (flights: Array<FlightDto>, showLoading: boolean) => {
        if (flights?.length) {
            const mapRequest = {
                clazz: SOL49_MAP_FLIGHTS_DATA_REQUEST_DTO_CLAZZ_VALUE,
                dataset: AppContext.getDataset(),
                flights: flights.map(flight => flight.flightId),
                retrieveVpdFlightData: true,
                flightProfileTrafficType: TrafficType.LOAD,
                retrieveCurrentFlightAirspaceCrossingsWithLoad: true,
                monitoringDataVersion: this.props.dashboardDataVersion,
                // retrieveVpdOtherAirspaceOfInterest: true,
            } as Sol49MapFlightsDataRequestDto;

            if (showLoading) {
                this.props.setLoadingData(1);
            }

            ServiceFactory.sol49Service()
                .getMapFlightsData(mapRequest)
                .then(reply => {
                    const highlightedSegments = this.mergeMap(
                        this.getHighlightedSegments(),
                        this.computeMapFlightDataWithTvLoads(reply.data),
                    );
                    this.setState({
                        flightData: reply.data,
                        highlightedSegments: highlightedSegments, //keep highlighted segments from props.tvsOccupancyPeriods
                    });
                })
                .catch(reason => {
                    notification.error({ message: 'Error', description: reason.message, duration: 0 });
                })
                .finally(() => {
                    if (showLoading) {
                        this.props.setLoadingData(-1);
                    }
                });
        } else {
            this.setState({
                flightData: {
                    flights: [],
                    airspaces: [],
                    tvMapLocations: [],
                    vpdFlightDataList: [],
                },
            });
        }
    };

    mergeMap<K, V>(map1: Map<K, Array<V>>, map2: Map<K, Array<V>>): Map<K, Array<V>> {
        let result = new Map<K, Array<V>>();
        if (map1) {
            result = new Map<K, Array<V>>(map1);
        }
        if (map2) {
            for (const key of Array.from(map2.keys())) {
                if (result.has(key)) {
                    result.set(key, [...result.get(key), ...map2.get(key)]);
                } else {
                    result.set(key, map2.get(key));
                }
            }
        }
        //TODO deconflict?
        return result;
    }

    //Using the highlighted segments to show the trajectory line based on tv load
    computeMapFlightDataWithTvLoads(input: MapFlightsDataDto) {
        const highlightedSegments = new Map<FlightId, ProfileSegmentHighlight[]>();
        for (const flight of input.flights) {
            if (flight['currentProfileAirspacePoints']) {
                const flightId = flight.flightId;
                const aspCrossings: Array<Sol49FlightAirspaceCrossing> = flight['currentProfileAirspacePoints'];
                if (aspCrossings.length > 0) {
                    for (const aspCrossing of aspCrossings.filter(
                        sol49AspCrossing => sol49AspCrossing.countType === this.state.display,
                    )) {
                        let loadClassification: PathPointLoadClassification;
                        // TODO(bjarne): Store loadValue.nonRelevant in the segment as well?
                        switch (aspCrossing.loadValue.loadClassification) {
                            case LoadClassification.OVERLOAD:
                                loadClassification = PathPointLoadClassification.OVERLOAD;
                                break;
                            case LoadClassification.HIGH_THRESHOLD:
                                loadClassification = PathPointLoadClassification.HIGH;
                                break;
                            case LoadClassification.LOW_THRESHOLD:
                                loadClassification = PathPointLoadClassification.LOW;
                                break;
                        }
                        if (loadClassification) {
                            const aspCrossingTimePeriod = {
                                from: aspCrossing.firstEntryTime,
                                to: aspCrossing.lastExitTime,
                            };
                            const aspCrossingLoadIntersectingTimePeriod = TimePeriodUtils.intersection(
                                aspCrossingTimePeriod,
                                aspCrossing.loadValue.timePeriod,
                            );
                            let segments: ProfileSegmentHighlight[] = highlightedSegments.get(flightId);
                            if (!segments) {
                                segments = [];
                                highlightedSegments.set(flightId, segments);
                            }
                            segments.push({
                                loadClassification: loadClassification,
                                // timePeriod: {from: aspCrossing.firstEntryTime, to: aspCrossing.lastExitTime}
                                timePeriod: aspCrossingLoadIntersectingTimePeriod,
                            });
                        }
                    }
                }
            }
        }
        // Remove duplicate segments - happens when a TV that is not monitorable is regulated (segments overlap)
        const deconflictedHighlightedSegments = new Map<FlightId, ProfileSegmentHighlight[]>();
        highlightedSegments.forEach((value, key) => {
            const valueAfter = this.deconflictHighlightedSegments(value);
            deconflictedHighlightedSegments.set(key, valueAfter);
        });
        return deconflictedHighlightedSegments;
    }

    refreshMapTrafficVolumeData = (tvs: Array<ExtendedMonitoredTv>) => {
        if (tvs?.length) {
            //TODO use cache
            const request: MapTrafficVolumeListRequest = {
                tvIdList: tvs.map(tv => tv.refLocation.id),
                dataset: AppContext.getDataset(),
            };
            ServiceFactory.coreService()
                .getMapTrafficVolumeList(request)
                .then(result => {
                    if (!result.error) {
                        this.setState({
                            selectedMapTVs: result.data,
                        });
                    }
                });
        } else {
            this.setState({ selectedMapTVs: [] });
        }
    };

    refreshMeasureMapTrafficVolumeData = (measures: Array<MonitoredMeasure>) => {
        if (measures?.length) {
            const measuresTvs: Map<string, string> = new Map<string, string>();
            for (const measure of measures.filter(measure => MapLayerUtils.isValidMapMeasure(measure))) {
                measuresTvs.set(
                    measure.measure.measureId.id,
                    MapLayerUtils.MONITORED_MEASURE_TV_ID_PROVIDER.getKey(measure),
                );
            }
            if (measuresTvs?.size > 0) {
                //TODO use cache
                const request: MapTrafficVolumeListRequest = {
                    tvIdList: Array.from(measuresTvs.values()),
                    dataset: AppContext.getDataset(),
                };
                ServiceFactory.coreService()
                    .getMapTrafficVolumeList(request)
                    .then(result => {
                        if (!result.error) {
                            const defPerTv = result.data.reduce((map, mtv) => {
                                map.set(mtv.trafficVolume.id, mtv);
                                return map;
                            }, new Map<string, MapTrafficVolume>());
                            const selectedMeasureMapTVs = Array.from(measuresTvs.keys()).map(measureId =>
                                Object.assign(defPerTv.get(measuresTvs.get(measureId)), { measureId: measureId }),
                            );
                            this.setState({
                                selectedMeasureMapTVs: selectedMeasureMapTVs,
                            });
                            this.nmui4dContext?.unityRequests.sendMeasuresToUnity(
                                selectedMeasureMapTVs,
                                this.props.monitoredMeasures,
                                this.state.flightLevelFilter,
                            );
                        }
                    })
                    .catch(reason => this.nmui4dContext?.logger.error(reason));
            } else {
                this.setState({ selectedMeasureMapTVs: [] });
                this.nmui4dContext?.unityRequests.sendMeasuresToUnity(
                    [],
                    this.props.monitoredMeasures,
                    this.state.flightLevelFilter,
                );
            }
        } else {
            this.setState({ selectedMeasureMapTVs: [] });
            this.nmui4dContext?.unityRequests.sendMeasuresToUnity(
                [],
                this.props.monitoredMeasures,
                this.state.flightLevelFilter,
            );
        }
    };

    /**
     * Builds a map of profile segments to be highlighted for flight, based on the monitored load values of traversed TVs.
     */
    private getHighlightedSegments(): Map<FlightId, ProfileSegmentHighlight[]> {
        const highlightedSegments = new Map<FlightId, ProfileSegmentHighlight[]>();
        if (this.props.tvsOccupancyPeriods && this.props.monitoredTvs) {
            for (const tvId in this.props.tvsOccupancyPeriods) {
                const monitoredTv = this.props.monitoredTvs.find(monitoredTv => tvId === monitoredTv.refLocation.id);

                if (monitoredTv) {
                    // Could be null if the TV is outside of the area of responsibility
                    this.props.tvsOccupancyPeriods[tvId].forEach((occupancyPeriod: TimePeriod, flightId: FlightId) => {
                        for (const mlv of monitoredTv.loadValues) {
                            // Exclude MLVs that are outside of the activation period
                            if (
                                MonitoredLoadUtils.getActivePeriodForOccupancy(
                                    monitoredTv.activePeriods,
                                    mlv.timePeriod,
                                ) &&
                                TimePeriodUtils.intersects(mlv.timePeriod, occupancyPeriod)
                            ) {
                                // Parts of the trajectories that are outside of the monitored period are de-facto ignored
                                let loadClassification: PathPointLoadClassification;
                                // TODO(bjarne): Store loadValue.nonRelevant in the segment as well?
                                switch (mlv.loadClassification) {
                                    case LoadClassification.OVERLOAD:
                                        loadClassification = PathPointLoadClassification.OVERLOAD;
                                        break;
                                    case LoadClassification.HIGH_THRESHOLD:
                                        loadClassification = PathPointLoadClassification.HIGH;
                                        break;
                                    case LoadClassification.LOW_THRESHOLD:
                                        loadClassification = PathPointLoadClassification.LOW;
                                        break;
                                }
                                if (loadClassification) {
                                    let segments: ProfileSegmentHighlight[] = highlightedSegments.get(flightId);
                                    if (!segments) {
                                        segments = [];
                                        highlightedSegments.set(flightId, segments);
                                    }
                                    segments.push({
                                        loadClassification: loadClassification,
                                        timePeriod: TimePeriodUtils.intersection(mlv.timePeriod, occupancyPeriod),
                                    });
                                }
                            }
                        }
                    });
                }
            }
        }

        // Remove duplicate segments - happens when a TV that is not monitorable is regulated (segments overlap)
        const deconflictedHighlightedSegments = new Map<FlightId, ProfileSegmentHighlight[]>();
        highlightedSegments.forEach((value, key) => {
            const valueAfter = this.deconflictHighlightedSegments(value);
            deconflictedHighlightedSegments.set(key, valueAfter);
        });
        return deconflictedHighlightedSegments;
    }

    deconflictHighlightedSegments = (segments: ProfileSegmentHighlight[]): ProfileSegmentHighlight[] => {
        const highlightedSegments = segments.slice();
        const splitTimes: Date[] = [];
        highlightedSegments.forEach(value => {
            Utils.addAllIfNeeded(splitTimes, [value.timePeriod.from, value.timePeriod.to]);
        });
        splitTimes.sort(TimeUtils.compare);

        const deconflictedHighlightedSegments: ProfileSegmentHighlight[] = [];
        for (let i = 0; i < splitTimes.length - 1; i++) {
            const timePeriod = { from: splitTimes[i], to: splitTimes[i + 1] };

            for (let j = 0; j < highlightedSegments.length; j++) {
                if (TimePeriodUtils.contains(highlightedSegments[j].timePeriod, timePeriod)) {
                    // TODO: Loop over all segments, and keep the one with the worst colour
                    deconflictedHighlightedSegments.push({
                        loadClassification: highlightedSegments[j].loadClassification,
                        timePeriod: timePeriod,
                    });
                    break;
                }
            }
        }
        return deconflictedHighlightedSegments;
    };

    //TODO this method is duplicated (here and in the NetworkingMonitoringFlows)
    getFilteredMonitoredTvList = memoizeOne(
        (monitoredTvs: Array<ExtendedMonitoredTv>, tvSetMap: { [key: string]: string }, textFilter: string) => {
            // TODO: Move to utility class?
            if (textFilter) {
                const textFilterUC: Array<string> = Utils.toUpperCase(textFilter).split(' ');
                return monitoredTvs?.filter(monitoredTv => {
                    const tvId = Utils.toUpperCase(monitoredTv.refLocation.id);
                    const tvSet = Utils.toUpperCase(tvSetMap[tvId]);
                    //if the query text has a * uses the simpleWildcardEquals, otherwise checks if a tv or tvSet contains the given query text
                    const matchingTextFilters = textFilterUC.filter(textFilterEl => {
                        const re = Utils.wildcardToRegex(textFilterEl);
                        return re.test(tvId) || re.test(tvSet);
                    });
                    //return tvId?.indexOf(textFilterUC) >= 0 || tvSet?.indexOf(textFilterUC) >= 0
                    return matchingTextFilters.length > 0;
                });
            }
            return monitoredTvs;
        },
    );

    private defaultMapRefTimePeriod = memoizeOne((monitoredPeriod: TimePeriod, currentTime: Date): TimePeriod => {
        return { from: currentTime, to: monitoredPeriod.to };
    });

    public componentDidMount() {
        this.nmui4dContext = this.context as NMUI4DContextProps;
    }

    componentDidUpdate(prevProps: NetworkMonitoringMapProps, prevState: NetworkMonitoringMapState): void {
        if (this.props.visible) {
            if (!prevProps?.visible || Utils.isNotEquals(prevProps?.selectedFlights, this.props.selectedFlights)) {
                // The selected flights data doesn't contain point profile, which is needed - the data gets retrieved and then displayed

                // => Selected flights array is getting updated at every data refresh (every ~1mn).
                //    I don't try to be clever here, and retrieve the point profiles every time, which is not costly because the data comes from a flight cache.
                //only show the loading spinner if the user as actually changed the selected flights and it isn't an auto refresh
                const newFlightsSelected = !Utils.isShallowEquals(
                    prevProps?.selectedFlights?.map(flight => flight.flightId.id),
                    this.props.selectedFlights?.map(flight => flight.flightId.id),
                );
                this.refreshMapFlightData(this.props.selectedFlights, newFlightsSelected);
            }

            if (
                !prevProps?.visible ||
                Utils.isNotEquals(prevProps?.selectedTvs, this.props.selectedTvs) ||
                Utils.isNotEquals(prevProps?.selectedHotspots, this.props.selectedHotspots)
            ) {
                // The selected flights data doesn't contain point profile, which is needed - the data gets retrieved and then displayed

                // => Selected tvs array is getting updated at every data refresh (every ~1mn).
                this.refreshMapTrafficVolumeData([...this.props.selectedTvs, ...this.props.selectedHotspots]);
            }

            if (!prevProps?.visible || Utils.isNotEquals(prevProps?.selectedMeasures, this.props.selectedMeasures)) {
                this.refreshMeasureMapTrafficVolumeData(this.props.selectedMeasures);
            }

            if (
                !prevProps?.visible ||
                Utils.isNotShallowEquals(prevProps?.tvsOccupancyPeriods, this.props.tvsOccupancyPeriods)
            ) {
                const highlightedSegments = this.getHighlightedSegments();
                this.setState({ highlightedSegments: highlightedSegments });
            }

            if (Utils.isNotExists(this.state.mapRefTime)) {
                this.setState({ mapRefTime: this.props.currentTime, refreshMapTime: true });
            } else {
                if (this.state.refreshMapTime) {
                    this.setState({ mapRefTime: this.props.currentTime });
                } else {
                    this.updateMapRefTime(this.state.mapRefTime, this.props.monitoredPeriod, this.props.currentTime);
                }
            }

            if (Utils.isNotExists(this.state.mapRefTimePeriod)) {
                this.setState({
                    mapRefTimePeriod: this.defaultMapRefTimePeriod(this.props.monitoredPeriod, this.props.currentTime),
                    refreshMapTimePeriod: true,
                });
            } else {
                if (this.state.refreshMapTimePeriod) {
                    const newPeriod = this.defaultMapRefTimePeriod(this.props.monitoredPeriod, this.props.currentTime);
                    this.setState({
                        mapRefTimePeriod: newPeriod,
                    });
                    this.props.onMapRefTimePeriodChanged?.(newPeriod);
                } else {
                    if (
                        TimePeriodUtils.compareTo(this.state.mapRefTimePeriod, prevState.mapRefTimePeriod) !== 0 ||
                        TimePeriodUtils.compareTo(this.props.monitoredPeriod, prevProps.monitoredPeriod) !== 0
                    ) {
                        this.updateMapRefTimePeriod(
                            this.state.mapRefTimePeriod,
                            this.props.monitoredPeriod,
                            this.props.currentTime,
                        );
                    }
                }
            }
        } else {
            if (prevProps?.visible) {
                this.pauseAnimation();
                //Reset the state ref times, this way they get reset to current time when user opens the map again (Oussama)
                this.setState({
                    mapRefTime: undefined,
                    refreshMapTime: true,
                    mapRefTimePeriod: undefined,
                    refreshMapTimePeriod: true,
                });
            }
        }
    }

    private updateMapRefTimePeriod(mapRefTimePeriod: TimePeriod, monitoredPeriod: TimePeriod, currentTime: Date) {
        if (TimePeriodUtils.intersects(mapRefTimePeriod, monitoredPeriod)) {
            // Partial update of the mapRefTimePeriod to stay inside the monitored period
            const newPeriod = TimePeriodUtils.intersection(mapRefTimePeriod, monitoredPeriod);
            this.setState({ mapRefTimePeriod: newPeriod });
            this.props.onMapRefTimePeriodChanged?.(newPeriod);
        } else {
            if (currentTime.getDate() !== mapRefTimePeriod.from.getDate()) {
                // The day has changed, merge time and date
                const newPeriod = {
                    from: TimeUtils.mergeDateAndTime(currentTime, this.state.mapRefTimePeriod.from),
                    to: TimeUtils.mergeDateAndTime(currentTime, this.state.mapRefTimePeriod.to),
                };
                this.setState({
                    mapRefTimePeriod: newPeriod,
                });
                this.props.onMapRefTimePeriodChanged?.(newPeriod);
            } else {
                // Same day but new period is outside the ref, set value to monitored period
                this.setState({
                    mapRefTimePeriod: monitoredPeriod,
                    refreshMapTime: false,
                });
                this.props.onMapRefTimePeriodChanged?.(monitoredPeriod);
            }
        }
    }

    private updateMapRefTime(mapRefTime: Date, monitoredPeriod: TimePeriod, currentTime: Date) {
        if (!TimePeriodUtils.contains(monitoredPeriod, mapRefTime)) {
            this.setState({
                mapRefTime: currentTime,
                refreshMapTime: true,
            });
        }
    }

    private refreshFlightHighlightedSegments() {
        this.setState(prevState => {
            const highlightedSegments = this.mergeMap(
                this.getHighlightedSegments(),
                this.computeMapFlightDataWithTvLoads(prevState.flightData),
            );

            return {
                highlightedSegments: highlightedSegments, //keep highlighted segments from props.tvsOccupancyPeriods
            };
        });
    }

    render() {
        return (
            <Spin wrapperClassName="panel-spinner" spinning={this.props.loading > 0} size={'large'}>
                <FullscreenMapView
                    currentTime={this.props.currentTime}
                    timePeriod={this.props.monitoredPeriod}
                    selectedFlights={this.props.selectedFlights}
                    selectedTvs={this.props.selectedTvs}
                    monitoredTvs={this.getFilteredMonitoredTvList(
                        this.props.monitoredTvs,
                        this.props.tvSetMap,
                        this.props.textFilter,
                    )}
                    visible={this.props.visible && this.props.fullscreenMap}
                    convectionData={this.props.convectionData}
                    hotspots={this.props.hotspots}
                    selectedHotspots={this.props.selectedHotspots}
                    mapSettings={this.props.mapSettings}
                    onMapSettingsChanged={this.props.onMapSettingsChanged}
                    highlightedSegments={this.state?.highlightedSegments}
                    flightData={this.state?.flightData}
                    selectedMapTVs={this.state?.selectedMapTVs}
                    selectedMapMeasuresTVs={this.state?.selectedMeasureMapTVs}
                    mapRefTime={this.state?.mapRefTime}
                    onMapRefTimeChange={this.handleMapRefTimeChange}
                    flightLevelFilter={this.state.flightLevelFilter}
                    onFlightLevelFilterChange={this.handleFlRangeChange}
                    mapRefTimePeriod={this.state.mapRefTimePeriod}
                    onMapRefTimePeriodChange={this.handleMapRefTimePeriodChange}
                    monitoredMeasures={this.props.monitoredMeasures}
                    selectedMeasures={this.props.selectedMeasures}
                    regulationOutlook={this.props.regulationOutlook}
                    selectedRegulationOutlook={this.props.selectedRegulationOutlook}
                    timePeriodSelection={this.state.timePeriodSelection}
                    onTimePeriodSelectionChange={this.handleMapTimePeriodSelectionModeChange}
                    display={this.state.display}
                    onDisplayChange={this.handleFullscreenDisplayChange}
                    showTimeReset={!this.state.refreshMapTime}
                    showTimePeriodReset={!this.state.refreshMapTimePeriod}
                    onResetMapRefTime={this.handleResetMapRefTime}
                    onResetMapRefTimePeriod={this.handleResetMapRefTimePeriod}
                    filteredTvIds={this.props.filteredTvIds}
                    timeAnimation={this.state.timeAnimation}
                    onTimeAnimationChange={play => (play ? this.playAnimation() : this.pauseAnimation())}
                    showDwdTPShortcuts={AppContext.getConvectionSource() === ConvectionSource.DWD}
                />

                <SharedMapView
                    currentTime={this.props.currentTime}
                    timePeriod={this.props.monitoredPeriod}
                    selectedFlights={this.props.selectedFlights}
                    selectedTvs={this.props.selectedTvs}
                    controlDisplayOpts={{ boxSelection: true, scenarioPolygonSelection: true }}
                    monitoredTvs={this.getFilteredMonitoredTvList(
                        this.props.monitoredTvs,
                        this.props.tvSetMap,
                        this.props.textFilter,
                    )}
                    visible={this.props.visible && !this.props.fullscreenMap}
                    convectionData={this.props.convectionData}
                    mapSettings={this.props.mapSettings}
                    onMapSettingsChanged={this.props.onMapSettingsChanged}
                    highlightedSegments={this.state?.highlightedSegments}
                    flightData={this.state?.flightData}
                    selectedMapTVs={this.state?.selectedMapTVs}
                    selectedMapMeasuresTVs={this.state?.selectedMeasureMapTVs}
                    onMapRefTimeChange={this.handleMapRefTimeChange}
                    onMapRefTimePeriodChange={this.handleMapRefTimePeriodChange}
                    flightLevelFilter={this.state.flightLevelFilter}
                    onFlightLevelFilterChange={this.handleFlRangeChange}
                    mapRefTime={this.state?.mapRefTime}
                    mapRefTimePeriod={this.state.mapRefTimePeriod}
                    showTimeReset={!this.state.refreshMapTime}
                    showTimePeriodReset={!this.state.refreshMapTimePeriod}
                    onResetMapRefTime={this.handleResetMapRefTime}
                    onResetMapRefTimePeriod={this.handleResetMapRefTimePeriod}
                    timePeriodSelection={this.state.timePeriodSelection}
                    onTimePeriodSelectionChange={this.handleMapTimePeriodSelectionModeChange}
                    timeAnimation={this.state.timeAnimation}
                    onTimeAnimationChange={play => (play ? this.playAnimation() : this.pauseAnimation())}
                    showDwdTPShortcuts={AppContext.getConvectionSource() === ConvectionSource.DWD}
                />
            </Spin>
        );
    }

    playAnimation = () => {
        if (this.state.timePeriodSelection) {
            this.animationStartTime = this.state.mapRefTimePeriod.from;
            this.setState(
                { timeAnimation: true },
                () =>
                    (this.interval = setInterval(() => {
                        let nextFrom = TimeUtils.withoutMinutes(
                            TimeUtils.addMinutes(this.state.mapRefTimePeriod.from, 60),
                        );
                        if (TimeUtils.compare(nextFrom, this.state.mapRefTimePeriod.to) > 0) {
                            nextFrom = this.animationStartTime;
                        }
                        this.handleMapRefTimePeriodChange({ ...this.state.mapRefTimePeriod, from: nextFrom });
                    }, this.ANIMATION_INTERVAL_MS)),
            );
        } else {
            this.setState(
                { timeAnimation: true },
                () =>
                    (this.interval = setInterval(() => {
                        this.handleMapRefTimeChange(
                            TimeUtils.withoutMinutes(TimeUtils.addMinutes(this.state.mapRefTime, 60)),
                        );
                    }, this.ANIMATION_INTERVAL_MS)),
            );
        }
    };

    pauseAnimation = () => {
        this.setState({ timeAnimation: false }, () => clearInterval(this.interval));
    };

    // HANDLERS

    handleMapRefTimeChange = refTime => {
        this.setState({
            mapRefTime: this.forceDateInTimePeriod(refTime, this.props.monitoredPeriod),
            refreshMapTime: false,
        });
    };

    private handleFlRangeChange = (flRange: FlightLevelBand) => {
        this.setState({ flightLevelFilter: flRange });
        this.props.onFlightLevelFilterChanged?.(flRange);
    };

    handleMapRefTimePeriodChange = (value: TimePeriod) =>
        this.setState({
            mapRefTimePeriod: value,
            refreshMapTimePeriod: false,
        });

    handleMapTimePeriodSelectionModeChange = (value: boolean) => {
        this.setState(
            {
                timePeriodSelection: value,
                mapRefTime: value ? this.state.mapRefTime : this.state.mapRefTimePeriod.from,
                mapRefTimePeriod: !value
                    ? this.state.mapRefTimePeriod
                    : {
                          from: this.state.mapRefTime,
                          to:
                              this.state.mapRefTimePeriod.to.getTime() > this.state.mapRefTime.getTime()
                                  ? this.state.mapRefTimePeriod.to
                                  : this.props.monitoredPeriod.to,
                      },
                refreshMapTime: value ? this.state.refreshMapTime : this.state.refreshMapTimePeriod,
                refreshMapTimePeriod: !value ? this.state.refreshMapTimePeriod : this.state.refreshMapTime,
            },
            () => this.state.timeAnimation && this.pauseAnimation(),
        );
    };

    handleFullscreenDisplayChange = (value: CountType) => {
        this.setState(
            {
                display: value,
            },
            () => this.refreshFlightHighlightedSegments(),
        );

        this.props.onDisplayChanged?.(value);
    };

    handleResetMapRefTime = (): void => {
        this.setState({
            mapRefTime: this.props.currentTime,
            refreshMapTime: true,
        });
    };

    handleResetMapRefTimePeriod = (): void => {
        this.setState({
            mapRefTimePeriod: { from: this.props.currentTime, to: this.props.monitoredPeriod.to },
            refreshMapTimePeriod: true,
        });
    };
}

export default connector(NetworkMonitoringMap);
